import React, { ReactNode, useEffect, useMemo, useState } from 'react'

import { SxProps, Theme } from '@mui/material'

import { useTranslation } from 'language/translator'

import Icon from 'components_new/atoms/Icon'
import MenuItem from 'components_new/atoms/Menu/MenuItem'
import Table, { TableProps } from 'components_new/atoms/Table'
import TableBody from 'components_new/atoms/Table/TableBody'
import TableCell, { TableCellProps } from 'components_new/atoms/Table/TableCell'
import TableContainer from 'components_new/atoms/Table/TableContainer'
import TableFooter from 'components_new/atoms/Table/TableFooter'
import TableHead from 'components_new/atoms/Table/TableHead'
import TableRow from 'components_new/atoms/Table/TableRow'
import TableSortLabel from 'components_new/atoms/Table/TableSortLabel'

import EmptyPlaceholder from 'components_new/molecules/EmptyPlaceholder'
import LoadingSection from 'components_new/molecules/LoadingSection'

import {
  compareNormalized,
  extractFallback,
  normalizeSortValue,
  RowSortValue,
  SortDirection
} from './utils'

export interface SimpleTableProps {
  addRow?: {
    onClick: () => void
  }
  border?: boolean
  disableBorders?: boolean
  disablePadding?: boolean
  disableSorting?: boolean
  hover?: boolean
  columns?: {
    align?: TableCellProps['align']
    color?: string
    defaultSort?: SortDirection
    padding?: TableCellProps['padding']
    title?: ReactNode
    width?: number | string
  }[]
  emptyPlaceholder?: string
  footer?: {
    cells: ReactNode[]
  }
  loading?: boolean
  maxHeight?: number | string
  preview?: boolean
  rows: {
    cells: ReactNode[]
    href?: string
    id?: string
    onClick?: () => void
    pinned?: boolean
    search?: string
    sortValues?: RowSortValue[]
    sx?: SxProps
  }[]
  size?: TableProps['size']
  stickyFirstColumn?: boolean
  stickyHeader?: boolean
  sx?: SxProps
}

export const TABLE_PREVIEW_LENGTH = 3

const stickyFirstColumnSx = (isHeader: boolean) => ({
  position: 'sticky' as const,
  left: 0,
  zIndex: isHeader ? 3 : 1,
  bgcolor: 'background.paper',
  borderRight: '1px solid',
  borderColor: 'divider'
})

const SimpleTable = React.forwardRef((props: SimpleTableProps, ref: any) => {
  const {
    addRow,
    border,
    columns,
    disableBorders,
    disablePadding,
    disableSorting,
    emptyPlaceholder,
    footer,
    hover,
    loading,
    maxHeight,
    preview,
    rows,
    size,
    stickyFirstColumn,
    stickyHeader,
    sx
  } = props

  const { t } = useTranslation()

  const noHead = !columns || !columns.some((column) => column.title)

  // sort
  const defaultSortKey = useMemo(() => {
    if (!columns) return null
    const idx = columns.findIndex((col) => col.defaultSort)

    return idx !== -1 ? `${idx}:${columns[idx].defaultSort}` : null
  }, [columns])

  const [sortIndex, setSortIndex] = useState<number | null>(() => {
    if (!defaultSortKey) return null

    return Number(defaultSortKey.split(':')[0])
  })
  const [sortDirection, setSortDirection] = useState<SortDirection>(() => {
    if (!defaultSortKey) return 'asc'

    return defaultSortKey.split(':')[1] as SortDirection
  })

  useEffect(() => {
    if (defaultSortKey === null) {
      setSortIndex(null)
      setSortDirection('asc')
    } else {
      const [idx, dir] = defaultSortKey.split(':')

      setSortIndex(Number(idx))
      setSortDirection(dir as SortDirection)
    }
  }, [defaultSortKey])

  const sortableColumns = useMemo(() => {
    const count = columns?.length ?? 0

    return Array.from({ length: count }, (_, colIndex) =>
      rows.some((r) => {
        const sv = r.sortValues?.[colIndex]

        return sv && !sv.disabled
      })
    )
  }, [rows, columns?.length])

  const handleSortClick = (colIndex: number) => {
    if (!sortableColumns[colIndex]) return

    setSortIndex((prev) => {
      if (prev === colIndex) {
        setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'))

        return prev
      }
      setSortDirection('asc')

      return colIndex
    })
  }

  const sortedRows = useMemo(() => {
    const pinnedRows = rows.filter((r) => r.pinned)
    const unpinnedRows = rows.filter((r) => !r.pinned)

    if (sortIndex == null) return [...pinnedRows, ...unpinnedRows]
    if (!sortableColumns[sortIndex]) return [...pinnedRows, ...unpinnedRows]

    const dir = sortDirection === 'asc' ? 1 : -1

    return [
      ...pinnedRows,
      ...unpinnedRows
        .map((row, originalIndex) => ({ row, originalIndex }))
        .sort((a, b) => {
          const aSv = a.row.sortValues?.[sortIndex]
          const bSv = b.row.sortValues?.[sortIndex]

          // Treat disabled/missing values as "no value"
          const aHas = aSv && !aSv.disabled && aSv.value != null
          const bHas = bSv && !bSv.disabled && bSv.value != null

          // Put rows with real sort values first (optional behavior)
          if (aHas && !bHas) return -1
          if (!aHas && bHas) return 1

          const aVal = aHas
            ? normalizeSortValue(aSv.value)
            : extractFallback(a.row.cells[sortIndex])

          const bVal = bHas
            ? normalizeSortValue(bSv.value)
            : extractFallback(b.row.cells[sortIndex])

          const cmp =
            compareNormalized(aVal, bVal) ||
            compareNormalized(
              normalizeSortValue(aSv?.tiebreak),
              normalizeSortValue(bSv?.tiebreak)
            )

          if (cmp !== 0) return cmp * dir

          return a.originalIndex - b.originalIndex // stable
        })
        .map((x) => x.row)
    ]
  }, [rows, sortIndex, sortDirection, sortableColumns])

  const getCellSx = (j: number, cellCount: number) => ({
    color: columns ? columns[j].color : undefined,
    width: columns ? columns[j].width : undefined,
    ...(columns?.[j].width ? { whiteSpace: 'nowrap' } : null),
    ...(disablePadding
      ? {
          pl: j === 0 ? 0 : undefined,
          pr: j === cellCount - 1 ? 0 : undefined
        }
      : null),
    ...(stickyFirstColumn && j === 0
      ? stickyFirstColumnSx(false)
      : { position: 'relative' as const })
  })

  return (
    <TableContainer
      ref={ref}
      sx={{
        ...(border
          ? { border: '1px solid', borderColor: 'divider', borderRadius: 1 }
          : null),
        ...(maxHeight ? { maxHeight, overflow: 'auto' } : null),
        ...sx
      }}
    >
      <Table
        size={size ?? 'small'}
        stickyHeader={stickyHeader || !!maxHeight}
        sx={loading ? { height: '100%' } : undefined}
      >
        {noHead || preview ? null : (
          <TableHead>
            <TableRow>
              {columns.map((column, i) => {
                const sortable = sortableColumns[i]

                const showSortLabel = sortable && !disableSorting

                return (
                  <TableCell
                    align={column.align}
                    // Positional: titles are not unique — several
                    // columns carry an empty one.
                    key={i}
                    sx={{
                      width: column.width,
                      ...(column.width ? { whiteSpace: 'nowrap' } : null),
                      ...(stickyFirstColumn && i === 0
                        ? stickyFirstColumnSx(true)
                        : null),
                      ...(showSortLabel
                        ? {
                            cursor: 'pointer',
                            // Tints on top, a sticky header must stay opaque.
                            ['&:hover']: {
                              backgroundImage: (theme: Theme) =>
                                `linear-gradient(${theme.palette.action.hover}, ${theme.palette.action.hover})`
                            }
                          }
                        : null)
                    }}
                  >
                    {showSortLabel ? (
                      <TableSortLabel
                        active={sortIndex === i}
                        disabled={loading}
                        direction={sortIndex === i ? sortDirection : 'asc'}
                        onClick={() => handleSortClick(i)}
                        sx={{ width: '100%' }}
                      >
                        {column.title}
                      </TableSortLabel>
                    ) : (
                      // A column that cannot be sorted renders its title
                      // directly. The disabled sort button it used to sit
                      // in swallowed clicks, so an interactive header —
                      // a select-all checkbox — could never work.
                      column.title
                    )}
                  </TableCell>
                )
              })}
            </TableRow>
          </TableHead>
        )}

        <TableBody>
          {loading || rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={columns?.length}>
                {loading ? (
                  <LoadingSection sx={{ p: 2 }} />
                ) : (
                  <EmptyPlaceholder
                    title={emptyPlaceholder ?? t('global', 'no_options_found')}
                  />
                )}
              </TableCell>
            </TableRow>
          ) : (
            sortedRows
              .slice(0, preview ? TABLE_PREVIEW_LENGTH : sortedRows.length)
              .map((row, i) => (
                <TableRow
                  hover={hover || Boolean(row.onClick) || Boolean(row.href)}
                  href={row.href}
                  key={row.id ?? `row-${i}`}
                  noBorder={disableBorders}
                  onClick={row.onClick}
                  search={row.search}
                  sx={row.sx}
                >
                  {row.cells.map((cell, j) => (
                    <TableCell
                      align={columns ? columns[j].align : 'left'}
                      padding={columns ? columns[j].padding : undefined}
                      key={`row-${i}-cell-${j}`}
                      sx={getCellSx(j, row.cells.length)}
                    >
                      {cell}
                    </TableCell>
                  ))}
                </TableRow>
              ))
          )}

          {addRow ? (
            <TableRow
              sx={
                maxHeight
                  ? {
                      position: 'sticky',
                      bottom: 0,
                      zIndex: 1,
                      bgcolor: 'background.paper'
                    }
                  : undefined
              }
            >
              <TableCell
                colSpan={
                  columns ? columns.length : rows ? rows[0].cells.length : 1
                }
                sx={{ p: 0 }}
              >
                <MenuItem
                  component="button"
                  disabled={loading}
                  onClick={addRow.onClick}
                  sx={{
                    height: 48,
                    width: '100%',
                    fontWeight: 500,
                    justifyContent: 'center',
                    gap: 0.5
                  }}
                >
                  <Icon fontSize="small" name="AddCircleOutlineOutlined" />
                  {t('global', 'add')}
                </MenuItem>
              </TableCell>
            </TableRow>
          ) : null}
        </TableBody>

        {footer && !loading && !preview ? (
          <TableFooter>
            <TableRow noBorder={disableBorders}>
              {footer.cells.map((cell, j) => (
                <TableCell
                  align={columns ? columns[j].align : 'left'}
                  key={`footer-cell-${j}`}
                  sx={getCellSx(j, footer.cells.length)}
                >
                  {cell}
                </TableCell>
              ))}
            </TableRow>
          </TableFooter>
        ) : null}
      </Table>
    </TableContainer>
  )
})

SimpleTable.displayName = 'SimpleTable'
export default SimpleTable
